import { Controller, useFormContext } from "react-hook-form"
import { DualColumnRow, ExternalLink } from "../../../../../../style"
import { Trans, t } from "@lingui/macro"
import { useEffect, useMemo } from "react"

import { DropdownField } from "../../../../../../../../../../components/DropdownField"
import { ReceiverForm } from "../ReceiverForm"
import { ShippingMethodPData } from "../../../../types"
import { TextField } from "../../../../../../../../../../components/TextField"
import debounce from "lodash/debounce"
import { i18n } from "@lingui/core"
import { useGeolocation } from "../../../../../../../../providers/GeoLocationProvider/GeolocationContext"

export const ShippingMethodP = () => {
  const {
    control,
    errors,
    setValue,
    watch,
  } = useFormContext<ShippingMethodPData>()
  const language = i18n.locale
  const {
    data,
    loading,
    fetchGeolocation,
    resetGeolocation,
    errorLocation,
  } = useGeolocation()

  const postalCode = watch("destinationPostalCode")

  const debouncedFetchGeolocation = useMemo(
    () =>
      debounce(code => {
        fetchGeolocation(code)
      }, 500),
    [fetchGeolocation]
  )

  const stateOptions = useMemo(
    () =>
      postalCode && data?.objects.states
        ? data.objects.states.map(state => ({
            id: state.stateId,
            name: state.name,
            value: state.name,
          }))
        : [],
    [postalCode, data?.objects.states]
  )

  const cityOptions = useMemo(
    () =>
      postalCode && data?.objects.cities
        ? data.objects.cities.map((city, i) => ({
            id: i.toString(),
            name: city.name,
            value: city.name,
          }))
        : [],
    [postalCode, data?.objects.cities]
  )

  useEffect(() => {
    setValue("destinationState", stateOptions[0]?.value)
    if (!postalCode) {
      setValue("destinationState", "")
    }
  }, [setValue, stateOptions, postalCode])

  useEffect(() => {
    setValue("destinationCity", cityOptions[0]?.value)
    if (!postalCode) {
      setValue("destinationCity", "")
    }
  }, [setValue, cityOptions, postalCode])

  useEffect(() => {
    if (errorLocation) {
      setValue("destinationState", "")
      setValue("destinationCity", "")
    }
  }, [setValue, errorLocation])

  useEffect(() => {
    return () => {
      resetGeolocation()
    }
  }, [resetGeolocation])

  const handlePostalCodeChange = (postalCode: string) => {
    const minLength = language === "es-AR" ? 4 : 5

    if (postalCode.length >= minLength) {
      debouncedFetchGeolocation(postalCode)
    } else if (data !== null) {
      resetGeolocation()
    }
  }

  return (
    <>
      <ReceiverForm
        title={t`¿Quién retira?`}
        fields={{
          receiverPhone: false,
        }}
      />

      <DualColumnRow>
        <Controller
          control={control}
          name="destinationPostalCode"
          defaultValue=""
          rules={{
            required: t`Completá el campo código postal.`,
          }}
          render={({ onChange, value }) => (
            <TextField
              name="destinationPostalCode"
              label={<Trans>Código Postal</Trans>}
              errorMsg={errorLocation || errors.destinationPostalCode?.message}
              maxLength={language === "es-AR" ? 4 : 5}
              hideLength={true}
              value={value}
              onChange={e => {
                const newValue = e.target.value.replace(/[^0-9]/g, "") // Solo permitir números

                handlePostalCodeChange(newValue)
                onChange(newValue)
              }}
              disabled={loading}
              extraErrorMargin={8}
            />
          )}
        />
        <ExternalLink
          rel="noopener noreferrer"
          href={t`https://www.correoargentino.com.ar/formularios/cpa`}
          target="_blank"
        >
          <Trans>No conozco mi código postal</Trans>
        </ExternalLink>
      </DualColumnRow>
      <Controller
        control={control}
        name="destinationCity"
        defaultValue=""
        rules={{
          required: true,
        }}
        render={props => (
          <DropdownField
            name="destinationCity"
            label={<Trans>Localidad</Trans>}
            errorMsg={errors.destinationCity?.message}
            options={cityOptions}
            disabled={cityOptions.length < 2}
            loading={loading}
            {...props}
          />
        )}
      />
      <Controller
        control={control}
        name="destinationState"
        defaultValue=""
        rules={{
          required: true,
        }}
        render={({ onChange, value }) => (
          <DropdownField
            name="destinationState"
            label={<Trans>Provincia</Trans>}
            options={stateOptions}
            errorMsg={errors.destinationState?.message}
            disabled={stateOptions.length < 2}
            value={value}
            loading={loading}
            onChange={selectedStateName => {
              onChange(selectedStateName)

              const selectedState = stateOptions.find(
                state => state.name === selectedStateName
              )

              if (postalCode && selectedState && stateOptions.length > 1) {
                fetchGeolocation(postalCode, selectedState.id)
              }
            }}
          />
        )}
      />
    </>
  )
}
